import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, Clock, Users, FileCheck, BarChart3, Building2, Key, CircleDot, XCircle, MinusCircle, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { seoContent, content } from '../data/caseStudyVisitorSystemData';

const CaseStudyVisitorSystem = () => {
  const { language: lang } = useLanguage();

  const seo = seoContent[lang] || seoContent.de;
  const t = content[lang];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FA] to-white pt-20">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/case-study/visitor-system"
        lang={lang}
      />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#C8D8E8]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 text-[#2E5E99] hover:text-[#1E4F85] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backLink}
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#2E5E99] text-white text-sm font-medium rounded-full">
              {t.title}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
            {t.subtitle}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Image */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/visitor/images/img_1_1.jpeg" 
              alt={t.subtitle}
              className="w-full h-[400px] object-cover"
            />
          </div>
        </motion.section>

        {/* Ausgangssituation */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.situation.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.situation.text}</p>
            <ul className="space-y-2 mb-4">
              {t.situation.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CircleDot className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.situation.conclusion}</p>
          </div>
        </motion.section>

        {/* Herausforderung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.challenge.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.challenge.text}</p>
            <ul className="space-y-2 mb-4">
              {t.challenge.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.challenge.extra}</p>
          </div>
        </motion.section>

        {/* Lösung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-[#E7F0FA] to-white rounded-xl p-6 md:p-8 border border-[#2E5E99]/30 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#2E5E99]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.solution.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.solution.text}</p>
            <p className="text-[#0D2440] font-medium mb-3">{t.solution.subtitle}</p>
            <ul className="space-y-2 mb-4">
              {t.solution.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#2E5E99] font-medium">{t.solution.conclusion}</p>
          </div>
        </motion.section>

        {/* Integration */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.integration.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.integration.text}</p>
            <ul className="space-y-2 mb-4">
              {t.integration.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] font-medium">{t.integration.conclusion}</p>
          </div>
        </motion.section>

        {/* Multi-Site */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.multisite.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-6">{t.multisite.text}</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* General Part */}
              <div className="bg-[#E7F0FA] rounded-lg p-4">
                <h4 className="font-semibold text-[#0D2440] mb-2">{t.multisite.general.title}</h4>
                <p className="text-[#5A7A9B] text-sm mb-3">{t.multisite.general.text}</p>
                <ul className="space-y-1">
                  {t.multisite.general.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[#5A7A9B] text-sm">
                      <span className="text-[#2E5E99] mt-1">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
                <p className="text-[#2E5E99] text-sm font-medium mt-3">{t.multisite.general.note}</p>
              </div>
              
              {/* Specific Part */}
              <div className="bg-cyan-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#0D2440] mb-2">{t.multisite.specific.title}</h4>
                <p className="text-[#5A7A9B] text-sm mb-3">{t.multisite.specific.text}</p>
                <ul className="space-y-1">
                  {t.multisite.specific.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[#5A7A9B] text-sm">
                      <span className="text-cyan-600 mt-1">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-semibold text-green-700 mb-2">{t.multisite.efficiency.title}</h4>
              <p className="text-[#5A7A9B] text-sm">{t.multisite.efficiency.text}</p>
            </div>
          </div>
        </motion.section>

        {/* Ergebnis */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.result.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-6">{t.result.text}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.result.items.map((item) => (
                <div key={item.title} className="bg-green-50 rounded-lg p-4 border border-green-100">
                  <h4 className="font-semibold text-[#0D2440] mb-2 text-sm">{item.title}</h4>
                  <p className="text-[#5A7A9B] text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Vorher/Nachher Vergleich */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-[#0D2440] text-center mb-8">{t.comparison.title}</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Vorher */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h4 className="text-xl font-semibold text-red-700 mb-4">{t.comparison.before.title}</h4>
              <ul className="space-y-3">
                {t.comparison.before.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <MinusCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Nachher */}
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h4 className="text-xl font-semibold text-green-700 mb-4">{t.comparison.after.title}</h4>
              <ul className="space-y-3">
                {t.comparison.after.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <PlusCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="bg-gradient-to-r from-[#2E5E99] to-[#1E4F85] rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {t.cta.text}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=visitor">
                <Button className="bg-white text-[#2E5E99] hover:bg-white/90 px-8 py-3 h-auto text-base">
                  {t.cta.button}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/visitor-system">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 h-auto text-base">
                  {t.cta.systemLink}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default CaseStudyVisitorSystem;
